import { ButtonGroup, Button, Typography, Box } from '@mui/material';
import dayjs from 'dayjs';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import { getStartAndEndDateFromRange } from '../utils';
import { useEffect, useState } from 'react';
import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  aggregation: string;
  setAggregation: any;
  handleFetchCustomDateRangeData: ({
    aggregation,
  }: {
    aggregation: string;
  }) => void;
  handleFetchCustomOnAggregationChange: ({
    aggregation,
    startDate,
    endDate,
  }: {
    aggregation: string;
    startDate: Date;
    endDate: Date;
  }) => void;
  isLoading?: boolean;
}

const FatigueDateRangeDiv = ({
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  aggregation,
  setAggregation,
  handleFetchCustomDateRangeData,
  handleFetchCustomOnAggregationChange,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const [aggregationOptions, setAggregationOptions] = useState([
    'Daily',
    'Monthly',
  ]);

  const handleSelect = (id: string) => {
    setIsSelected(id);

    let newStartDate = startDate;
    let newEndDate = yesterday;

    switch (id) {
      case 'Today': {
        const startDateVal = new Date().toISOString().slice(0, 10);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(startDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case '1D': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(1);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case '7D': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(7);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case '14D': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(14);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case '1M': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(30);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case 'Custom':
        break;
      default:
        break;
    }

    if (id !== 'Custom') {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    }
  };

  const dateRangeList = ['Today', '1D', '7D', '14D', '1M', 'Custom'];

  const dateRangeLabel = (item: string): string => {
    const map: Record<string, string> = {
      Today: t('date_range_today'),
      '1D': t('date_range_1d'),
      '7D': t('date_range_7d'),
      '14D': t('date_range_14d'),
      '1M': t('date_range_1m'),
      Custom: t('date_range_custom'),
    };
    return map[item] ?? item;
  };

  const aggregationLabel = (item: string): string => {
    const map: Record<string, string> = {
      Daily: t('aggregation_daily'),
      Monthly: t('aggregation_monthly'),
    };
    return map[item] ?? item;
  };

  useEffect(() => {
    if (!startDate || !endDate) return;
    const days = dayjs(endDate).diff(dayjs(startDate), 'day');
    if (days > 30) {
      setAggregationOptions(['Daily', 'Monthly']);
    } else {
      if (aggregation === 'Monthly') {
        setAggregation('Daily');
      }
      setAggregationOptions(['Daily']);
    }
  }, [startDate, endDate]);

  return (
    <Box
      sx={{
        margin: '20px 0',
        backgroundColor: 'transparent',
        padding: '5px 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '15px',
        }}
      >
        <div
          style={{
            color: palette.textSecondary,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Typography
            style={{ fontSize: '11px', marginRight: '8px', fontWeight: 600 }}
          >
            {t('quick_range_10')}
          </Typography>
          <ButtonGroup
            size="small"
            variant="outlined"
            aria-label={t('basic_button_group')}
            sx={{
              border: `1px solid ${palette.borderGrey}`,
              '& .MuiButtonGroup-grouped': {
                borderColor: palette.borderGrey,
              },
            }}
          >
            {dateRangeList.map((item) => {
              return (
                <Button
                  key={item}
                  size="small"
                  disabled={isLoading}
                  sx={{
                    fontSize: '10px',
                    fontWeight: 600,
                    minWidth: '60px',
                    backgroundColor:
                      isSelected === item
                        ? palette.primary
                        : enableDarkTheme
                        ? palette.toolBarBackground
                        : palette.menuOptions,
                    color: isSelected === item ? '#fff' : palette.textSecondary,
                    '&:hover': {
                      backgroundColor:
                        isSelected === item
                          ? palette.primaryDark
                          : enableDarkTheme
                          ? palette.boxBackground
                          : palette.sectionBackground,
                    },
                  }}
                  onClick={() => {
                    handleSelect(item);
                  }}
                >
                  {dateRangeLabel(item)}
                </Button>
              );
            })}
          </ButtonGroup>
        </div>

        {isSelected === 'Custom' && (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <CustomDateRangePicker
              startDate={startDate}
              endDate={endDate}
              onFinalDateChange={handleDateChange}
              maxDate={dayjs(yesterday)}
              format={aggregation === 'Monthly' ? 'MMM YYYY' : 'YYYY-MM-DD'}
              views={aggregation === 'Monthly' ? ['month', 'year'] : ['day']}
              disabled={isLoading}
              autoOpenEnabled={false}
              sx={{
                minWidth: '148px',
                maxWidth: '148px',
                width: '148px',
              }}
            />
            <Button
              onClick={() => {
                handleFetchCustomDateRangeData({ aggregation });
              }}
              disabled={isLoading || !startDate || !endDate}
              sx={{
                fontSize: '11px',
                backgroundColor: palette.primary,
                color: '#fff',
                padding: '4px 12px',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: palette.primaryDark,
                },
                '&.Mui-disabled': {
                  backgroundColor: enableDarkTheme
                    ? palette.borderGrey
                    : palette.lightGrey,
                  color: enableDarkTheme ? palette.grey : palette.textSecondary,
                },
              }}
            >
              {t('apply')}
            </Button>
          </Box>
        )}

        {isSelected === 'Custom' && (
          <div
            style={{
              marginLeft: '10px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Typography
              sx={{
                fontSize: '11px',
                color: palette.textSecondary,
                fontWeight: 600,
              }}
            >
              {t('aggregation')}
            </Typography>

            <ProductionSelector
              options={aggregationOptions}
              selected={aggregation}
              setSelected={(value: string) => {
                setAggregation(value);
                if (isSelected === 'Custom') {
                  let sD = dayjs(startDate).toDate();
                  let eD = dayjs(endDate).toDate();
                  if (value === 'Monthly') {
                    sD = dayjs(startDate).startOf('month').toDate();
                    eD = dayjs(endDate).endOf('month').toDate();
                  }
                  handleFetchCustomOnAggregationChange({
                    aggregation: value,
                    startDate: sD,
                    endDate: eD,
                  });
                }
              }}
              disabled={isLoading}
              minWidth="100px"
              getOptionLabel={aggregationLabel}
            />
          </div>
        )}
      </div>
    </Box>
  );
};

export default FatigueDateRangeDiv;
